import { Gifs, Stickers } from "@bundle/core";
import type { GifRef, StickerRef } from "@bundle/contracts";
import { useT } from "../i18n/I18nProvider.tsx";
import { Image, Pressable, Stack, Text } from "../primitives/index.ts";

/** GIFs in messages fit this box. */
const GIF_MAX_WIDTH = 360;
const GIF_MAX_HEIGHT = 260;

export type StickerMessageProps = {
  sticker: StickerRef;
  /** Tap: open its pack (view, add to My stickers); absent = not tappable (pending messages). */
  onPress?: () => void;
  testID: string;
};

/** A sticker message: drawn large (up to 160 pt) without a bubble; animated stickers play. */
export function StickerMessage({ sticker, onPress, testID }: StickerMessageProps) {
  const t = useT();
  const size = Stickers.stickerDisplaySize(sticker.width, sticker.height);
  const image = <Image src={sticker.url} alt={t("message.sticker", { name: sticker.name })} width={size.width} height={size.height} fit="contain" />;
  if (onPress === undefined) {
    return (
      <Stack align="start" testID={testID}>
        {image}
      </Stack>
    );
  }
  return (
    <Stack align="start" testID={testID}>
      <Pressable onPress={onPress} radius="md" accessibilityLabel={t("message.sticker", { name: sticker.name })} testID={`${testID}-open`}>
        {image}
      </Pressable>
    </Stack>
  );
}

export type GifMessageProps = { gif: GifRef; testID: string };

/** A GIF message: the provider's animated rendition (hotlinked) with its attribution under it. */
export function GifMessage({ gif, testID }: GifMessageProps) {
  const t = useT();
  const size = Gifs.gifDisplaySize(gif, GIF_MAX_WIDTH, GIF_MAX_HEIGHT);
  return (
    <Stack align="start" gap="0.5" testID={testID}>
      <Image src={gif.url} alt={t("message.gif", { title: gif.title })} width={size.width} height={size.height} radius="md" />
      <Text variant="caption" color="text.tertiary">
        {t("message.gifVia")}
      </Text>
    </Stack>
  );
}
